import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Users, Building2, Calendar, TrendingUp, Activity, FileText } from 'lucide-react';
import AdminNavbar from '../../../AdminNavbar';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { doctorService, bookingService, clinicService, statsService } from '../../../../services';
import { useDataFetching } from '../../../../hooks';
import { calculateStats } from '../../../../utils';
import { demoBookings, demoClinics } from '../../../../constants';
import { doctorsDemoData } from '../../../lib/data/doctors';

const AdminDashboard = () => {
  // Use custom hooks for data fetching
  const { data: doctorsData, isLoading: doctorsLoading } = useDataFetching(
    doctorService.getAllDoctors,
    doctorsDemoData,
    { errorMessage: 'Failed to load doctors. Using demo data.' }
  );

  const { data: bookingsData, isLoading: bookingsLoading } = useDataFetching(
    bookingService.getAllBookings,
    demoBookings,
    { errorMessage: 'Failed to load bookings. Using demo data.' }
  );

  const { data: clinicsData, isLoading: clinicsLoading } = useDataFetching(
    clinicService.getAllClinics,
    demoClinics,
    { errorMessage: 'Failed to load clinics. Using demo data.' }
  );

  // Fetch dashboard statistics
  const { data: dashboardStats, isLoading: statsLoading } = useDataFetching(
    statsService.getDashboardStats,
    {
      totalUsers: 0,
      totalDoctors: doctorsData?.length || 0,
      totalClinics: clinicsData?.length || 0,
      totalAppointments: bookingsData?.length || 0,
      totalRevenue: 0,
      monthlyGrowth: 0
    },
    { errorMessage: 'Failed to load dashboard statistics. Using calculated data.' }
  );

  // Fetch booking statistics
  const { data: bookingStats, isLoading: bookingStatsLoading } = useDataFetching(
    bookingService.getBookingStats,
    {
      totalAppointments: bookingsData?.length || 0,
      pendingAppointments: 0,
      completedAppointments: 0,
      cancelledAppointments: 0,
      totalRevenue: 0
    },
    { errorMessage: 'Failed to load booking statistics. Using calculated data.' }
  );

  // Fetch doctor statistics
  const { data: doctorStats, isLoading: doctorStatsLoading } = useDataFetching(
    doctorService.getDoctorStats,
    {
      totalDoctors: doctorsData?.length || 0,
      activeDoctors: 0,
      inactiveDoctors: 0,
      averageRating: 0,
      totalSpecialties: 0
    },
    { errorMessage: 'Failed to load doctor statistics. Using calculated data.' }
  );

  // Fetch clinic statistics
  const { data: clinicStats, isLoading: clinicStatsLoading } = useDataFetching(
    clinicService.getClinicStats,
    {
      totalClinics: clinicsData?.length || 0,
      activeClinics: 0,
      inactiveClinics: 0,
      totalTypes: 0
    },
    { errorMessage: 'Failed to load clinic statistics. Using calculated data.' }
  );

  const isLoading = doctorsLoading || bookingsLoading || clinicsLoading || statsLoading || 
                   bookingStatsLoading || doctorStatsLoading || clinicStatsLoading;

  // Use API statistics or fallback to calculated data
  const totalDoctors = doctorStats?.totalDoctors || doctorsData?.length || 0;
  const totalBookings = bookingStats?.totalAppointments || bookingsData?.length || 0;
  const totalClinics = clinicStats?.totalClinics || clinicsData?.length || 0;
  const totalRevenue = bookingStats?.totalRevenue || dashboardStats?.totalRevenue || 0;
  const monthlyGrowth = dashboardStats?.monthlyGrowth || 0;
  

  
  // Booking status statistics - use API data or calculate from local data
  const bookingStatusStats = bookingStats?.pendingAppointments ? {
    pending: bookingStats.pendingAppointments,
    confirmed: bookingStats.completedAppointments,
    completed: bookingStats.completedAppointments,
    cancelled: bookingStats.cancelledAppointments
  } : calculateStats(bookingsData || [], 'status');

  // Doctor specialties statistics
  const specialtyStats = calculateStats(doctorsData || [], 'acfData.main_category');

  // Chart data for booking statuses
  const bookingStatusData = [
    { name: 'Pending', value: bookingStatusStats.pending || 0, color: '#6b7280' },
    { name: 'Confirmed', value: bookingStatusStats.confirmed || 0, color: '#3b82f6' },
    { name: 'Completed', value: bookingStatusStats.completed || 0, color: '#10b981' },
    { name: 'Cancelled', value: bookingStatusStats.cancelled || 0, color: '#ef4444' }
  ];

  // Chart data for doctor specialties
  const specialtyData = Object.entries(specialtyStats).map(([specialty, count]) => ({
    specialty,
    count
  }));

  // Monthly booking trends (last 6 months)
  const monthlyData = [
    { month: 'Aug', bookings: 12 },
    { month: 'Sep', bookings: 18 },
    { month: 'Oct', bookings: 15 },
    { month: 'Nov', bookings: 22 },
    { month: 'Dec', bookings: 28 },
    { month: 'Jan', bookings: 25 }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavbar />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600 mt-2">Manage your healthcare platform</p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 mb-8">
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Doctors</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalDoctors}</div>
              <p className="text-xs text-muted-foreground">
                Active healthcare professionals
              </p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Bookings</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalBookings}</div>
              <p className="text-xs text-muted-foreground">
                Patient appointments
              </p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Confirmed Bookings</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{bookingStatusStats.confirmed || 0}</div>
              <p className="text-xs text-muted-foreground">
                Confirmed appointments
              </p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Bookings</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{bookingStatusStats.pending || 0}</div>
              <p className="text-xs text-muted-foreground">
                Awaiting confirmation
              </p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Clinics</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalClinics}</div>
              <p className="text-xs text-muted-foreground">
                Healthcare facilities
              </p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">₹{totalRevenue.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                {monthlyGrowth > 0 ? `+${monthlyGrowth}%` : `${monthlyGrowth}%`} from last month
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Booking Status Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Booking Status Distribution</CardTitle>
              <CardDescription>
                Breakdown of bookings by status
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={bookingStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {bookingStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Doctor Specialties Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Doctor Specialties</CardTitle>
              <CardDescription>
                Distribution of doctors by specialty
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={specialtyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="specialty" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Monthly Trends Chart */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Monthly Booking Trends</CardTitle>
            <CardDescription>
              Booking trends over the last 6 months
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="bookings" stroke="#3b82f6" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Doctors</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">Manage Doctors</div>
              <p className="text-xs text-muted-foreground">
                Add, edit, and manage doctor profiles
              </p>
              <div className="mt-4">
                <Link to="/admin/doctors">
                  <Button className="w-full">Manage Doctors</Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Clinics</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">Manage Clinics</div>
              <p className="text-xs text-muted-foreground">
                Add and manage clinic information
              </p>
              <div className="mt-4">
                <Link to="/admin/clinics">
                  <Button className="w-full">Manage Clinics</Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Bookings</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">Manage Bookings</div>
              <p className="text-xs text-muted-foreground">
                View and manage patient bookings
              </p>
              <div className="mt-4">
                <Link to="/admin/bookings">
                  <Button className="w-full">Manage Bookings</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mt-8">
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
              <CardDescription>
                Common administrative tasks
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                <Link to="/admin/doctors/create">
                  <Button variant="outline">
                    <Users className="h-4 w-4 mr-2" />
                    Add New Doctor
                  </Button>
                </Link>
                <Link to="/admin/clinics/create">
                  <Button variant="outline">
                    <Building2 className="h-4 w-4 mr-2" />
                    Add New Clinic
                  </Button>
                </Link>
                <Link to="/admin/bookings">
                  <Button variant="outline">
                    <Calendar className="h-4 w-4 mr-2" />
                    View All Bookings
                  </Button>
                </Link>
                <Link to="/admin/doctors">
                  <Button variant="outline">
                    <FileText className="h-4 w-4 mr-2" />
                    View All Doctors
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
